import {FormEvent, useEffect, useState} from "react";
import {Alert, Badge, Button, Card, Col, Form, ProgressBar, Row} from "react-bootstrap";
import {ProfileApi} from "../../api/ProfileApi";
import {UserViewModel} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {emailProblem, MIN_PASSWORD_LENGTH, newPasswordProblem} from "../../components/format";
import {Field, FieldControl} from "../../components/Field";
import {TwoFactorCard} from "./TwoFactorCard";
import {Avatar} from "../../components/Avatar";
import {AVATAR_MAX_DIMENSION, prepareAvatar} from "../../components/avatarImage";
import {account, format, rich, validation} from "../../i18n";
import {usePasswordConfirmation} from "../../components/PasswordConfirmation";

export function ProfilePage() {
    const {data: profile, error, loading, setData} = useAsync(() => ProfileApi.get(), []);
    return (
        <>
            <PageHeader title={account.profile.title}/>
            {loading && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            {profile && (
                <Row className="g-4">
                    <Col lg={6}><DetailsCard profile={profile} onSaved={setData}/></Col>
                    <Col lg={6}><EmailCard profile={profile}/></Col>
                    <Col lg={6}><PasswordCard/></Col>
                    <Col lg={6}><TwoFactorCard profile={profile} onChanged={mfa => setData(x => x && { ...x, mfa })}/></Col>
                </Row>
            )}
        </>
    );
}

function DetailsCard({profile, onSaved}: { profile: UserViewModel, onSaved(profile: UserViewModel): void }) {
    const {renew} = useAuth();
    const [displayName, setDisplayName] = useState(profile.displayName);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => setDisplayName(profile.displayName), [profile.displayName]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            // The API replaces every profile field: send the current values of the ones this form doesn't edit.
            const updated = await ProfileApi.update({
                displayName: displayName.trim(),
                timezone: profile.timezone,
                language: profile.language,
                defaultProjectId: profile.defaultProjectId
            });
            onSaved(updated);
            setSaved(true);
            // The navbar shows the name from the access token.
            await renew().catch(() => undefined);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">{account.profile.details.title}</Card.Title>
                <AvatarField profile={profile} onChanged={onSaved}/>
                <ErrorAlert error={error}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>{account.profile.details.saved}</Alert>}
                <Form onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="profile-username">
                        <Form.Label>{account.profile.details.username}</Form.Label>
                        <Form.Control value={profile.username} readOnly plaintext/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="profile-display-name">
                        <Form.Label>{account.profile.details.displayName}</Form.Label>
                        <Form.Control required maxLength={100} value={displayName} onChange={e => setDisplayName(e.target.value)}/>
                    </Form.Group>
                    <SubmitButton busy={busy}>{account.profile.details.save}</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}

// Choosing a file uploads it straight away: no preview, which would need a blob: URL (not allowed by the CSP) and
// would play an animated GIF. What comes back is the stored picture.
function AvatarField({profile, onChanged}: { profile: UserViewModel, onChanged(profile: UserViewModel): void }) {
    const {renew} = useAuth();
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState<number>();
    const [problem, setProblem] = useState<string>();
    const [error, setError] = useState<unknown>();
    const [inputKey, setInputKey] = useState(0);

    const run = async (action: () => Promise<UserViewModel>) => {
        setBusy(true);
        setError(undefined);
        try {
            onChanged(await action());
            // The navbar shows the avatar from the access token.
            await renew().catch(() => undefined);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
            setInputKey(x => x + 1); // clears the file input
        }
    };

    const choose = async (file: File | undefined) => {
        setProblem(undefined);
        setError(undefined);
        if (!file) {
            return;
        }
        const prepared = await prepareAvatar(file);
        if ('problem' in prepared) {
            setProblem(prepared.problem);
            setInputKey(x => x + 1);
            return;
        }
        setProgress(0);
        await run(() => ProfileApi.setAvatar(prepared.image, setProgress));
    };

    return (
        <div className="d-flex flex-wrap align-items-start gap-3 mb-4">
            <Avatar url={profile.avatarUrl} size="lg"/>
            <div className="flex-grow-1">
                <Form.Group controlId="profile-avatar">
                    <Form.Label>{account.profile.avatar.label}</Form.Label>
                    <Form.Control key={inputKey} type="file" size="sm" disabled={busy} accept="image/png,image/jpeg,image/gif"
                                  isInvalid={!!problem} aria-invalid={!!problem || undefined}
                                  aria-describedby={problem ? 'profile-avatar-help profile-avatar-error' : 'profile-avatar-help'}
                                  onChange={e => void choose((e.target as HTMLInputElement).files?.[0])}/>
                    <Form.Control.Feedback type="invalid" id="profile-avatar-error">{problem}</Form.Control.Feedback>
                    <Form.Text id="profile-avatar-help">
                        {format(account.profile.avatar.help, { max: AVATAR_MAX_DIMENSION })}
                    </Form.Text>
                </Form.Group>
                {progress !== undefined && <ProgressBar className="mt-2" now={progress * 100} aria-label={account.profile.avatar.progress}/>}
                <ErrorAlert error={error}/>
                {profile.avatarUrl && (
                    <Button variant="link" size="sm" className="px-0 text-danger" disabled={busy} onClick={() => run(ProfileApi.removeAvatar)}>
                        {account.profile.avatar.remove}
                    </Button>
                )}
            </div>
        </div>
    );
}

function EmailCard({profile}: { profile: UserViewModel }) {
    const confirmPassword = usePasswordConfirmation();
    const [email, setEmail] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [sentTo, setSentTo] = useState<string>();
    const [error, setError] = useState<unknown>();
    const problem = emailProblem(email);

    const sendLink = async (address: string) => {
        setBusy(true);
        setError(undefined);
        try {
            // A new address may need the password again; closing that dialog leaves the form as it was.
            if (await confirmPassword(currentPassword => ProfileApi.changeEmail(address, currentPassword))) {
                setSentTo(address);
                setEmail('');
                setValidated(false);
            }
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!problem) {
            await sendLink(email.trim());
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">{account.profile.email.title}</Card.Title>
                <p className="mb-2">
                    {rich(account.profile.email.current, { strong: text => <strong>{text}</strong> }, { email: profile.emailAddress })}{' '}
                    {profile.emailConfirmed
                        ? <Badge bg="success-subtle" text="success-emphasis">{account.profile.email.confirmed}</Badge>
                        : <Badge bg="warning-subtle" text="warning-emphasis">{account.profile.email.notConfirmed}</Badge>}
                    {profile.emailUndeliverable && <> <Badge bg="danger-subtle" text="danger-emphasis">{account.profile.email.notDelivering}</Badge></>}
                </p>
                {profile.emailUndeliverable && <Alert variant="warning" className="small">{account.profile.email.notDeliveringHelp}</Alert>}
                <p className="small text-body-secondary">
                    {profile.emailConfirmed
                        ? account.profile.email.confirmedHelp
                        : account.profile.email.notConfirmedHelp}
                </p>
                {(!profile.emailConfirmed || profile.emailUndeliverable) && !sentTo && (
                    <Button variant="outline-primary" size="sm" className="mb-3" disabled={busy} onClick={() => sendLink(profile.emailAddress)}>
                        {account.profile.email.confirmThis}
                    </Button>
                )}
                <ErrorAlert error={error} shownFields={['emailAddress']}/>
                {sentTo && (
                    <Alert variant="info" dismissible onClose={() => setSentTo(undefined)}>
                        {rich(account.profile.email.sent, { strong: text => <strong>{text}</strong> }, { email: sentTo })}
                        {sentTo.toLowerCase() !== profile.emailAddress.toLowerCase() && account.profile.email.sentUntilThen}
                    </Alert>
                )}
                <Form noValidate onSubmit={submit}>
                    <Field className="mb-3" controlId="profile-new-email" label={account.profile.email.newEmail} error={(validated && problem) || fieldError(error, 'emailAddress')}>
                        <FieldControl type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)}/>
                    </Field>
                    <SubmitButton busy={busy}>{account.profile.email.submit}</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}

function PasswordCard() {
    const [current, setCurrent] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();
    const problem = newPasswordProblem(password, confirm);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem || !current) {
            return;
        }
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            await ProfileApi.changePassword(current, password);
            setSaved(true);
            setCurrent('');
            setPassword('');
            setConfirm('');
            setValidated(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">{account.profile.password.title}</Card.Title>
                <ErrorAlert error={error} shownFields={['password', 'newPassword']}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>{account.profile.password.changed}</Alert>}
                <Form noValidate onSubmit={submit}>
                    <Field className="mb-3" controlId="profile-current-password" label={account.profile.password.current}
                           error={(validated && !current && account.profile.password.currentRequired) || fieldError(error, 'password')}>
                        <FieldControl type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)}/>
                    </Field>
                    <Field className="mb-3" controlId="profile-new-password" label={account.profile.password.newPassword} help={format(validation.passwordHelp, { min: MIN_PASSWORD_LENGTH })}
                           error={(validated && password.length < MIN_PASSWORD_LENGTH && validation.passwordShort) || fieldError(error, 'newPassword')}>
                        <FieldControl type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)}/>
                    </Field>
                    <Field className="mb-3" controlId="profile-confirm-password" label={account.profile.password.confirmPassword} error={validated && problem}>
                        <FieldControl type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)}/>
                    </Field>
                    <SubmitButton busy={busy}>{account.profile.password.submit}</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}
